import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Platform } from '@app/classes';
import { DATE_FORMAT } from '@app/constants';
import { AnalyticEvents } from '@app/enums';
import { Employee } from '@app/models/employee/employee.model';
import { ApplicantInterviews } from '@app/models/recruiting/applicant-interviews.model';
import { Applicant } from '@app/models/recruiting/applicant.model';
import { Interview } from '@app/models/recruiting/interview.model';
import { AnalyticService, AuthService, NotifyService } from '@app/services';
import moment from 'moment';
import { Subscription } from 'rxjs';
import {
    CheckboxFormGroup,
    requireCheckboxesToBeCheckedValidator,
} from '../../functions/require-checkbox-validator.function';
import { Attach } from '../../interfaces/interviews-assign.interface';
import { ApplicantProfileService, InterviewService } from '../../services';
import { ApplicantInterviewsTable } from '../../services/tables/applicant-interviews-table/applicant-interviews.table';

type Interviewer = {
    employee: Employee;
    isSelected: boolean;
};

interface InterviewFormGroup {
    interviewSelect: FormControl<number | null>;
    myCheckboxGroup: CheckboxFormGroup;
    feedbackDueDate: FormControl<moment.Moment | null>;
}

@Component({
    templateUrl: './interviews.template.html',
    styleUrls: ['./interviews.style.scss'],
})
export class InterviewsComponent implements OnInit, OnDestroy {
    applicant: Applicant;
    interviews: Interview[] = [];
    interviewers: Interviewer[] = [];
    applicantInterviews: ApplicantInterviews[] = [];
    applicantProfileSubscription: Subscription;
    applicantInterviewsTable: ApplicantInterviewsTable;
    formGroup: FormGroup<InterviewFormGroup>;
    minDate = new Date();

    isAssignModalOpen = false;
    isLoading = false;
    hasRecruiterAccess = false;

    constructor(
        private applicantProfileService: ApplicantProfileService,
        private interviewService: InterviewService,
        private auth: AuthService,
        private notify: NotifyService,
        private analyticService: AnalyticService
    ) {
        this.applicantProfileSubscription = this.applicantProfileService.applicantSubject.subscribe(
            (applicant: Applicant) => {
                this.applicant = applicant;
            }
        );

        this.applicantInterviewsTable = new ApplicantInterviewsTable(this.applicant);
    }

    get interviewSelect(): FormControl<number | null> {
        return this.formGroup.controls.interviewSelect;
    }

    get feedbackDueDate(): FormControl<moment.Moment | null> {
        return this.formGroup.controls.feedbackDueDate;
    }

    async ngOnInit(): Promise<void> {
        this.hasRecruiterAccess = this.auth.can(Platform.recruiting.permission.recruiter);

        this.fetchData();
    }

    async onSave(form: FormGroup<InterviewFormGroup>): Promise<void> {
        if (!form.valid) {
            return;
        }

        const attach: Attach = {
            interviewers: this.interviewers
                .filter((interviewer: Interviewer) => interviewer.isSelected)
                .map((interviewer: Interviewer) => interviewer.employee.id),
        };

        try {
            await this.interviewService.assignApplicantToInterview(
                this.interviewSelect.value,
                this.feedbackDueDate.value.format(DATE_FORMAT),
                this.applicant.id,
                attach
            );

            this.analyticService.trackEvent(
                AnalyticEvents.RecrutingApplciantInterviewsAssignInterviewClickAssignToSave
            );

            this.applicantInterviewsTable.reload();
        } catch (err) {
            this.notify.error(err.error?.errors[0]?.detail || 'applicant-tracker.cannotAssignInterview');
        } finally {
            this.fetchData();
        }

        this.isAssignModalOpen = false;
    }

    onCancelAssigningInteriewers(): void {
        this.isAssignModalOpen = false;
    }

    openAssignModal(): void {
        this.isAssignModalOpen = true;
    }

    ngOnDestroy(): void {
        this.applicantProfileSubscription.unsubscribe();
    }

    private async fetchData(): Promise<void> {
        this.isLoading = true;

        try {
            this.createFormGroup();

            await this.fetchInterviews();
            await this.fetchApplicantInterviews();

            // Only want unassinged interviews to be in the dropdown.
            this.interviews = this.filterAssignedInterviews(this.interviews);

            this.setHiringTeamMembers();
        } catch (err) {
            this.notify.error(err);
        }

        this.isLoading = false;
    }

    private async fetchInterviews(): Promise<void> {
        const [interviews] = await Interview.where('atsJobId', this.applicant.jobPostingId).get();

        this.interviews = interviews;
    }

    private async fetchApplicantInterviews(): Promise<void> {
        const [applicantInterviews] = await ApplicantInterviews.where('ats_applicant_id', this.applicant.id).get();

        this.applicantInterviews = applicantInterviews;
    }

    /**
     * TODO: [SQE-3687] Create filter for returning only unassigned interviews
     * @param interviews
     * @returns only non-assigned interviews
     */
    private filterAssignedInterviews(interviews: Interview[]): Interview[] {
        return interviews.filter((interview: Interview) => {
            return !this.applicantInterviews.find((applicantInterview: ApplicantInterviews) => {
                return applicantInterview.atsInterviewId === interview.id;
            });
        });
    }

    private setHiringTeamMembers(): void {
        this.interviewers = this.applicant.jobPosting.hiringTeam.map((employee: Employee) => {
            return { employee, isSelected: false };
        });
    }

    private createFormGroup(): void {
        const group = {};

        this.applicant.jobPosting.hiringTeam.forEach((employee) => {
            group[employee.id] = new FormControl(false);
        });

        this.formGroup = new FormGroup({
            interviewSelect: new FormControl(null, [Validators.required]),
            myCheckboxGroup: new FormGroup({ ...group }, requireCheckboxesToBeCheckedValidator()),
            feedbackDueDate: new FormControl(moment(), [Validators.required]),
        });
    }
}
